import type {
  LeadFormFieldDto,
  Profile,
  ProfileTemplateContentDto,
} from '@up2stay/up2promo-api-client';
import type {
  ProfileModuleDto,
  ProfileTemplateFieldPoliciesDto,
} from '@up2stay/up2promo-api-client';
import {
  LeadFormVisibilityMode,
  ProfileLanguage,
  ProfileVisibility,
} from '@up2stay/up2promo-api-client';

import { mergeModulesForProfileEdit } from '@/core/lib/profile-module-entry-policies';
import {
  stringToTemplateTextField,
  templateTextFieldToString,
} from '@/core/lib/profile-template-text-field';
import { parseTemplateContent } from '@/core/lib/profile-template-content';
import { isFieldLocked } from '@/core/lib/profile-template-fields';
import {
  buildProfileStylingPayload,
  PROFILE_STYLING_FORM_DEFAULTS,
  stylingToFormSlice,
  type ProfileStylingFormSlice,
} from '@/core/lib/profile-styling-form';

export const templateFormSchemaFields = {
  jobTitle: null as string | null,
  company: null as string | null,
  location: null as string | null,
  showProfileImage: true,
  showUp2promoBranding: true,
  language: 'auto' as 'auto' | 'de' | 'en',
  visibility: 'unlisted' as 'private' | 'unlisted',
  leadFormEnabled: false,
  leadFormVisibilityMode: 'manual' as 'off' | 'manual' | 'auto_open',
  leadFormAutoOpenDelay: 0 as number | null,
  leadsAutoShareWithWorkspace: false,
  ...PROFILE_STYLING_FORM_DEFAULTS,
};

export type TemplateFormData = typeof templateFormSchemaFields;

export const TEMPLATE_FORM_FIELD_NAMES = [
  'jobTitle',
  'company',
  'location',
  'showProfileImage',
  'showUp2promoBranding',
  'accentColor',
  'layout',
  'theme',
  'headerStyle',
  'headerImage',
  'buttonShape',
  'language',
  'visibility',
  'leadFormEnabled',
  'leadFormVisibilityMode',
  'leadFormAutoOpenDelay',
  'leadsAutoShareWithWorkspace',
] as const satisfies ReadonlyArray<keyof TemplateFormData>;

function stylingSliceFromForm(
  form: Pick<
    TemplateFormData,
    | 'layout'
    | 'theme'
    | 'headerStyle'
    | 'headerImage'
    | 'buttonShape'
    | 'accentColor'
  >,
): ProfileStylingFormSlice {
  return {
    layout: form.layout ?? PROFILE_STYLING_FORM_DEFAULTS.layout,
    theme: form.theme ?? PROFILE_STYLING_FORM_DEFAULTS.theme,
    headerStyle: form.headerStyle ?? PROFILE_STYLING_FORM_DEFAULTS.headerStyle,
    headerImage: form.headerImage ?? null,
    buttonShape: form.buttonShape ?? null,
    accentColor: form.accentColor ?? null,
  };
}

export function contentToFormValues(
  content: ProfileTemplateContentDto,
): TemplateFormData {
  const leadForm = content.leadForm;
  const visibilityMode = leadForm?.visibilityMode;
  const styling = stylingToFormSlice(content.styling);
  return {
    jobTitle: templateTextFieldToString(content.jobTitle),
    company: templateTextFieldToString(content.company),
    location: templateTextFieldToString(content.location),
    showProfileImage: content.showProfileImage ?? true,
    showUp2promoBranding: content.showUp2promoBranding ?? true,
    language:
      (content.language as TemplateFormData['language']) ??
      ProfileLanguage.AUTO,
    visibility:
      content.visibility === ProfileVisibility.PRIVATE ? 'private' : 'unlisted',
    leadFormEnabled: leadForm?.enabled ?? false,
    leadFormVisibilityMode:
      visibilityMode === LeadFormVisibilityMode.OFF ||
      visibilityMode === undefined
        ? 'manual'
        : (visibilityMode as TemplateFormData['leadFormVisibilityMode']),
    leadFormAutoOpenDelay: leadForm?.autoOpenDelay ?? 0,
    leadsAutoShareWithWorkspace: content.leadsAutoShareWithWorkspace ?? false,
    ...styling,
  };
}

/** Maps a loaded profile to ProfileEditPage form values. */
export function profileToEditFormValues(profile: Profile): TemplateFormData {
  const styling = stylingToFormSlice(profile.styling);
  return {
    jobTitle: profile.jobTitle ?? null,
    company: profile.company ?? null,
    location: profile.location ?? null,
    showProfileImage:
      (profile as { showProfileImage?: boolean }).showProfileImage ?? true,
    showUp2promoBranding:
      (profile as { showUp2promoBranding?: boolean }).showUp2promoBranding ??
      true,
    language: (profile.language as TemplateFormData['language']) ?? 'auto',
    visibility:
      profile.visibility === ProfileVisibility.PRIVATE ? 'private' : 'unlisted',
    leadFormEnabled: profile.leadForm?.enabled ?? false,
    leadFormVisibilityMode:
      profile.leadForm?.visibilityMode === LeadFormVisibilityMode.OFF ||
      profile.leadForm?.visibilityMode === undefined
        ? 'manual'
        : (profile.leadForm
            .visibilityMode as TemplateFormData['leadFormVisibilityMode']),
    leadFormAutoOpenDelay: profile.leadForm?.autoOpenDelay ?? 0,
    leadsAutoShareWithWorkspace: profile.leadsAutoShareWithWorkspace === true,
    ...styling,
  };
}

function coalesceText(
  profileValue: string | null | undefined,
  templateValue: string | null | undefined,
): string | null {
  const trimmed = profileValue?.trim() ?? '';
  if (trimmed) return profileValue ?? null;
  return templateValue ?? null;
}

/**
 * Merges template content into profile edit form state:
 * - locked (festgeschrieben): profile value when synced, else effective template (default + member override)
 * - free (individuell): same prefill baseline when profile field is empty
 */
export function mergeProfileEditFromTemplate(args: {
  profile: Profile;
  templateContent: ProfileTemplateContentDto;
  fieldPolicies: ProfileTemplateFieldPoliciesDto;
  templateGoverned?: boolean;
}): { form: TemplateFormData; modules: ProfileModuleDto[] } {
  const profileForm = profileToEditFormValues(args.profile);
  const templateForm = contentToFormValues(
    parseTemplateContent(args.templateContent),
  );
  const profileModules = (args.profile.modules ??
    []) as unknown as ProfileModuleDto[];
  const templateModules = (args.templateContent.modules ??
    []) as unknown as ProfileModuleDto[];
  const { fieldPolicies: policies } = args;

  const form: TemplateFormData = { ...profileForm };

  form.jobTitle = isFieldLocked(policies, 'jobTitle')
    ? coalesceText(profileForm.jobTitle, templateForm.jobTitle)
    : (profileForm.jobTitle ?? null);
  form.company = isFieldLocked(policies, 'company')
    ? coalesceText(profileForm.company, templateForm.company)
    : (profileForm.company ?? null);
  form.location = isFieldLocked(policies, 'location')
    ? coalesceText(profileForm.location, templateForm.location)
    : (profileForm.location ?? null);

  // Keep ProfileEdit in sync with actual profile state. For individually editable
  // fields we must not reinsert template defaults when profile values are empty.
  form.showProfileImage = profileForm.showProfileImage;
  form.showUp2promoBranding = profileForm.showUp2promoBranding;
  form.accentColor =
    profileForm.accentColor ?? templateForm.accentColor ?? null;
  form.layout = profileForm.layout ?? templateForm.layout;
  form.theme = profileForm.theme ?? templateForm.theme;
  form.headerStyle = profileForm.headerStyle ?? templateForm.headerStyle;
  form.headerImage = profileForm.headerImage ?? templateForm.headerImage;
  form.buttonShape = profileForm.buttonShape ?? templateForm.buttonShape;
  form.language = profileForm.language;
  form.visibility = profileForm.visibility;
  form.leadsAutoShareWithWorkspace = profileForm.leadsAutoShareWithWorkspace;

  form.leadFormEnabled = profileForm.leadFormEnabled;
  form.leadFormVisibilityMode = profileForm.leadFormVisibilityMode;
  form.leadFormAutoOpenDelay = profileForm.leadFormAutoOpenDelay;

  const modules = args.templateGoverned
    ? mergeModulesForProfileEdit(profileModules, templateModules)
    : profileModules.length > 0
      ? profileModules
      : templateModules;

  return { form, modules };
}

/**
 * DEV-507: without `feature.profile_branding` a template keeps the up2promo
 * branding and the default accent. Preview and save use this so they match
 * the locked switch instead of a value stored before a downgrade.
 */
export function applyTemplateBrandingEntitlement(
  form: TemplateFormData,
  canUseProfileBranding: boolean,
): TemplateFormData {
  if (canUseProfileBranding) return form;
  return { ...form, showUp2promoBranding: true, accentColor: null };
}

export function formToTemplateContent(
  form: TemplateFormData,
  modules: ProfileModuleDto[],
  fixedLeadFormFields: LeadFormFieldDto[],
): ProfileTemplateContentDto {
  const leadFormEnabled = form.leadFormEnabled ?? false;
  const visibilityMode = form.leadFormVisibilityMode ?? 'manual';
  const autoOpenDelay =
    typeof form.leadFormAutoOpenDelay === 'number'
      ? form.leadFormAutoOpenDelay
      : (form.leadFormAutoOpenDelay ?? 0);

  return {
    jobTitle: stringToTemplateTextField(form.jobTitle ?? null),
    company: stringToTemplateTextField(form.company ?? null),
    location: stringToTemplateTextField(form.location ?? null),
    showProfileImage: form.showProfileImage ?? true,
    showUp2promoBranding: form.showUp2promoBranding ?? true,
    styling: buildProfileStylingPayload(stylingSliceFromForm(form)),
    language: form.language as ProfileLanguage,
    visibility:
      form.visibility === 'private'
        ? ProfileVisibility.PRIVATE
        : ProfileVisibility.UNLISTED,
    leadsAutoShareWithWorkspace: form.leadsAutoShareWithWorkspace ?? false,
    modules: modules as ProfileTemplateContentDto['modules'],
    leadForm: {
      enabled: leadFormEnabled,
      visibilityMode: leadFormEnabled
        ? (visibilityMode as LeadFormVisibilityMode)
        : LeadFormVisibilityMode.OFF,
      autoOpenDelay,
      fields: fixedLeadFormFields,
    },
  };
}
